/**
 * #981 stage 2 — the Yjs model shared by the browser and the collab server (browser-safe: no
 * node imports). Spec §8.1–§8.2 of docs/plans/2026-10-22_LARGE_CSV_FRAGMENTS_981.md.
 *
 * SLOTS. A column is a PHYSICAL SLOT, a string key. Source columns are '0'..String(columnCount-1);
 * an added column mints `a<clientID>.<clock>` (that client's Yjs clock at the add — monotonic per
 * client, so a slot is unique across clients and never reused, even after its column is
 * deleted). A slot is never removed from a row; deleting a column only drops it from `columns`.
 *
 * FRAGMENT doc (one per fragment room):
 *   - `rows`: Y.Array<Y.Map>, one Y.Map per row { src, fields } — never parallel arrays, so a
 *     concurrent insert keeps a row's src and fields together. `src` is the source record index
 *     in the fragment (-1 = an inserted row). `fields` is a Y.Map<string> slot → value holding
 *     only the slots that were set (no padding): two writers of one cell resolve last-write-wins
 *     to ONE value, and writes to different slots never conflict.
 *   - `meta`: { fragId, baseHash }.
 *
 * SESSION doc (one per file, `frag=_session`):
 *   - `columns`: Y.Array<Y.Map { slot, name }> in OUTPUT order. Delete removes the entry, rename
 *     edits the name, add appends a new slot. Rename / delete locate the column BY SLOT at call
 *     time, so a concurrent reorder or delete never hits the wrong column.
 *   - `rowCounts`: fragId → number, SERVER-WRITTEN ONLY (`setFragmentRowCount`): the collab
 *     server hosting a fragment room writes that fragment's `rows.length` after applying updates;
 *     clients never write it. Every replica of a fragment converges to the same rows, so the
 *     servers write identical values and last-write-wins converges. (Client-written per-client
 *     deltas were rejected: two clients deleting the SAME row both subtract one while Yjs removes
 *     it once.) A missing entry means the manifest's count (`fragmentRowCount`).
 *   The manifest generation (Postgres, csv_fragment_sessions) and dirty / conflict / saved state
 *   (a server message) are NOT in this doc.
 *
 * DETERMINISTIC SEED. Both seeders write under clientID 0 (the doc's own id restored in a
 * `finally`) and are pure functions of their input — (fragId, baseHash, records) and the
 * manifest — so two instances seeding the same content independently produce identical Yjs
 * items and converge without duplicating them. That holds because two seeders only ever meet
 * inside a room keyed by the base hash, whose fragment bytes are immutable. A seed refuses a
 * non-empty doc (any item, or pending structs / deletes), and builds every value before it
 * writes, so a throw mid-seed leaves the doc empty. Determinism also requires IDENTICAL seeding
 * code on every instance: a change to a seeder must change the room key / base format, so old
 * and new instances never seed the same room.
 */
import * as Y from 'yjs'

export type FragmentRow = { src: number; fields: Record<string, string> }
export type CsvColumn = { slot: string; name: string }

/** The part of the working copy's manifest the session seed reads. */
export interface LargeCsvSessionManifest {
  header: { fields: string[] }
  columnCount: number
}

const SEED_CLIENT_ID = 0

const rowsOf = (doc: Y.Doc) => doc.getArray<Y.Map<unknown>>('rows')
const columnsArray = (doc: Y.Doc) => doc.getArray<Y.Map<unknown>>('columns')
const rowCounts = (doc: Y.Doc) => doc.getMap<number>('rowCounts')

/** Run `write` as client 0 on an empty doc; the doc's own clientID is restored whatever happens. */
function seed(doc: Y.Doc, write: () => void): void {
  const store = doc.store
  if (store.clients.size !== 0 || Y.getState(store, SEED_CLIENT_ID) !== 0 || store.pendingStructs || store.pendingDs) {
    throw new Error('large-csv seed: the doc is not empty')
  }
  const own = doc.clientID
  try {
    doc.clientID = SEED_CLIENT_ID
    doc.transact(write)
  } finally {
    doc.clientID = own
  }
}

function newRow(src: number, fields: [string, string][]): Y.Map<unknown> {
  const f = new Y.Map<string>()
  for (const [slot, value] of fields) f.set(slot, value)
  const row = new Y.Map<unknown>()
  row.set('src', src)
  row.set('fields', f)
  return row
}

export function loadFragmentRows(doc: Y.Doc, records: { fields: string[] }[], meta: { fragId: string; baseHash: string }): void {
  // Built before the seed writes anything: a bad record throws with the doc still empty.
  const rows = records.map((r, i) => newRow(i, r.fields.map((v, slot) => [String(slot), v] as [string, string])))
  seed(doc, () => {
    const m = doc.getMap('meta')
    m.set('fragId', meta.fragId)
    m.set('baseHash', meta.baseHash)
    rowsOf(doc).insert(0, rows)
  })
}

export function fragmentRowsOf(doc: Y.Doc): FragmentRow[] {
  return rowsOf(doc).toArray().map((row) => ({
    src: row.get('src') as number,
    fields: (row.get('fields') as Y.Map<string>).toJSON() as Record<string, string>,
  }))
}

/** `slot` is the PHYSICAL slot (`columnsOf(session)[displayCol].slot`), never a display column. */
export function setCell(doc: Y.Doc, offset: number, slot: string, value: string): void {
  const row = rowsOf(doc).get(offset)
  if (!row) throw new RangeError(`large-csv setCell: no row at ${offset}`)
  ;(row.get('fields') as Y.Map<string>).set(slot, value)
}

/** Inserts `n` rows (src -1) with no fields set. */
export function insertRows(doc: Y.Doc, at: number, n: number): void {
  rowsOf(doc).insert(at, Array.from({ length: n }, () => newRow(-1, [])))
}

export function deleteRows(doc: Y.Doc, at: number, n: number): void {
  rowsOf(doc).delete(at, n)
}

function newColumn(slot: string, name: string): Y.Map<unknown> {
  const col = new Y.Map<unknown>()
  col.set('slot', slot)
  col.set('name', name)
  return col
}

export function seedSession(doc: Y.Doc, m: LargeCsvSessionManifest): void {
  const cols = Array.from({ length: m.columnCount }, (_, slot) => newColumn(String(slot), m.header.fields[slot] ?? ''))
  seed(doc, () => {
    columnsArray(doc).insert(0, cols)
  })
}

export function columnsOf(session: Y.Doc): CsvColumn[] {
  return columnsArray(session).toArray().map((c) => ({ slot: c.get('slot') as string, name: c.get('name') as string }))
}

/** Appends a column on a fresh slot (`a<clientID>.<clock>`) and returns that slot. */
export function addColumn(session: Y.Doc, name: string): string {
  const slot = `a${session.clientID}.${Y.getState(session.store, session.clientID)}`
  columnsArray(session).push([newColumn(slot, name)])
  return slot
}

function columnIndex(session: Y.Doc, slot: string): number {
  return columnsArray(session).toArray().findIndex((c) => c.get('slot') === slot)
}

/** Renames the column holding `slot`; false when it no longer exists (deleted concurrently). */
export function renameColumn(session: Y.Doc, slot: string, name: string): boolean {
  const i = columnIndex(session, slot)
  if (i < 0) return false
  columnsArray(session).get(i).set('name', name)
  return true
}

/** Removes the column holding `slot` (the slot is never reused); false when it is already gone. */
export function deleteColumn(session: Y.Doc, slot: string): boolean {
  const i = columnIndex(session, slot)
  if (i < 0) return false
  columnsArray(session).delete(i, 1)
  return true
}

/**
 * SERVER-ONLY: the collab server hosting `fragId`'s room records that fragment's `rows.length`
 * after applying updates. Clients never call this.
 */
export function setFragmentRowCount(session: Y.Doc, fragId: string, n: number): void {
  rowCounts(session).set(fragId, n)
}

/** The fragment's current row count: the server-written count, else its manifest rows. */
export function fragmentRowCount(session: Y.Doc, manifestRows: number, fragId: string): number {
  return rowCounts(session).get(fragId) ?? manifestRows
}

/** Every server-written row count: (fragment id, count) — the fragments whose count was ever written. */
export function fragmentRowCountEntries(session: Y.Doc): [string, number][] {
  return [...rowCounts(session).entries()]
}

/** A row's fields in output order; a slot the row doesn't hold is ''. */
export function projectRow(fields: Record<string, string>, cols: CsvColumn[]): string[] {
  return cols.map((c) => (Object.prototype.hasOwnProperty.call(fields, c.slot) ? fields[c.slot] : ''))
}
